import type { Turn } from '@/stores/useDStore'
import { useDStore } from '@/stores/useDStore'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import { DThinkingBlock } from './DThinkingBlock'
import { DDiffViewer } from './DDiffViewer'
import type { FinalDiff } from '@/lib/api'

interface DChatMessageProps {
  turn: Turn
  turnIndex: number        // Index in the turns array (for hideTurnDiffViewer)
  isCurrentTurn?: boolean  // True if this is the turn currently being processed
  isProcessing?: boolean   // True if workflow is actively processing
}

export function DChatMessage({ turn, turnIndex, isCurrentTurn = false, isProcessing = false }: DChatMessageProps) {
  const { hideTurnDiffViewer } = useDStore()
  const isUser = turn.role === 'user'

  // Don't show persisted node indicators for the current turn while processing
  // (they're shown in real-time in DChat.tsx instead)
  const showNodeIndicators = !isUser && !(isCurrentTurn && isProcessing)

  return (
    <div
      className={cn(
        'flex',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          'max-w-[85%]',
          isUser
            ? 'rounded-lg px-4 py-3 bg-foreground text-background'
            : 'py-3'
        )}
      >
        {/* Collapsed thinking block for assistant messages (D workflow has no Gatekeeper) */}
        {!isUser && turn.thinking && (
          <DThinkingBlock thinking={turn.thinking} isStreaming={false} />
        )}

        {/* Node indicators - ABOVE the response content */}
        {/* Zinsser - persisted indicator (shows if node ran, with optional thinking) */}
        {showNodeIndicators && turn.zinsserRan && (
          <div className="mb-2">
            {turn.zinsserThinking && (
              <DThinkingBlock thinking={turn.zinsserThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Zinsser</span>
            </div>
          </div>
        )}

        {/* D workflow does not use CutClutter node */}

        {/* StrengthenVerbs - persisted indicator */}
        {showNodeIndicators && turn.strengthenVerbsRan && (
          <div className="mb-2">
            {turn.strengthenVerbsThinking && (
              <DThinkingBlock thinking={turn.strengthenVerbsThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Verbs</span>
            </div>
          </div>
        )}

        {/* CheckLogic - persisted indicator */}
        {showNodeIndicators && turn.checkLogicRan && (
          <div className="mb-2">
            {turn.checkLogicThinking && (
              <DThinkingBlock thinking={turn.checkLogicThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Logic</span>
            </div>
          </div>
        )}

        {/* RhythmSound - persisted indicator (green = completed) */}
        {showNodeIndicators && turn.rhythmSoundRan && (
          <div className="mb-3">
            {turn.rhythmSoundThinking && (
              <DThinkingBlock thinking={turn.rhythmSoundThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Rhythm</span>
            </div>
          </div>
        )}

        {/* Response content */}
        {isUser ? (
          <div className="prose-chat-user text-sm max-w-none [&_*:last-child]:mb-0 [&_p]:mb-2">
            <ReactMarkdown>{turn.content.trim()}</ReactMarkdown>
          </div>
        ) : (
          <div className="prose-chat text-sm min-h-0 [&_*:last-child]:mb-0">
            <ReactMarkdown>{turn.content.trim()}</ReactMarkdown>
          </div>
        )}

        {/* Editing Summary - final diff viewer for this turn (below response) */}
        {!isUser && turn.showFinalDiff && turn.finalDiff && (
          <div className="mt-4">
            <DDiffViewer
              finalDiff={turn.finalDiff as FinalDiff}
              onClose={() => hideTurnDiffViewer(turnIndex)}
            />
          </div>
        )}

        <div
          className={cn(
            'text-[10px] mt-1.5 font-light tracking-wide',
            isUser ? 'text-background/50' : 'text-muted-foreground/70'
          )}
        >
          {new Date(turn.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  )
}
